/* import __COLOCATED_TEMPLATE__ from './sync-status.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { type SyncStatusData } from 'embercom/models/knowledge-hub/sync-status-data';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

interface Signature {
  Element: HTMLSpanElement;
  Args: {
    syncStatus: SyncStatusData;
  };
}

const REASON_KEY_MAP: Record<string, string> = {
  checksum_duplication: 'checksum-duplication',
  keyword_filtering: 'keyword-filtering',
  status_code_400: 'status-code-400',
  blocked_url: 'blocked-url',
  session_blocked: 'session-blocked',
  rate_limited: 'rate-limited',
  network_error: 'network-error',
  navigation_timeout: 'navigation-timeout',
  page_navigation_error: 'page-navigation-error',
  download_error: 'download-error',
  unknown_error: 'unknown-error',
  page_missing_in_this_run: 'page-missing-in-this-run',
};

export default class SyncStatusComponent extends Component<Signature> {
  @service declare intl: IntlService;

  get statusClasses(): string {
    let baseClasses = 'text-sm px-2 py-px rounded-full';

    if (!this.args.syncStatus) {
      return baseClasses;
    }

    switch (this.args.syncStatus.syncStatus) {
      case SyncStatus.LIVE:
      case SyncStatus.PENDING_VERIFICATION:
        return `${baseClasses} bg-success-container`;
      case SyncStatus.SYNCING:
        return `${baseClasses} bg-notice-container`;
      case SyncStatus.SYNCED:
        return `${baseClasses} bg-neutral-container`;
      case SyncStatus.ERROR:
        return `${baseClasses} bg-error-container`;
      case SyncStatus.EXCLUDED:
        return `${baseClasses} bg-neutral-container`;
      case SyncStatus.FILTERED:
        return `${baseClasses} bg-neutral-container`;
      default:
        return baseClasses;
    }
  }

  get statusText(): string {
    if (!this.args.syncStatus) {
      return '';
    }

    switch (this.args.syncStatus.syncStatus) {
      case SyncStatus.LIVE:
      case SyncStatus.PENDING_VERIFICATION:
        return 'knowledge-hub.filterable-list.list.cells.sync-status.live';
      case SyncStatus.SYNCING:
        return 'knowledge-hub.filterable-list.list.cells.sync-status.syncing';
      case SyncStatus.ERROR:
        return 'knowledge-hub.filterable-list.list.cells.sync-status.error';
      case SyncStatus.SYNCED:
        return 'knowledge-hub.filterable-list.list.cells.sync-status.synced';
      case SyncStatus.EXCLUDED:
      case SyncStatus.FILTERED:
        return 'knowledge-hub.filterable-list.list.cells.sync-status.excluded';
      default:
        return '';
    }
  }

  get shouldShowTooltip(): boolean {
    switch (this.args.syncStatus.syncStatus) {
      case SyncStatus.ERROR:
      case SyncStatus.EXCLUDED:
      case SyncStatus.FILTERED:
        return true;
      case SyncStatus.LIVE:
        if (this.showMissingWarning) {
          return true;
        }
        return false;
      default:
        return false;
    }
  }

  get tooltipHeader(): string | null {
    if (!this.shouldShowTooltip) {
      return null;
    }

    let reason = this.args.syncStatus?.filteredReason;
    if (!reason) {
      return null;
    }

    let keyPart = REASON_KEY_MAP[reason] || 'default';
    return this.intl.t(
      `knowledge-hub.filterable-list.list.cells.sync-status.reasons.${keyPart}.header`,
    );
  }

  get tooltipMessage(): string | null {
    if (!this.shouldShowTooltip) {
      return null;
    }

    let reason = this.args.syncStatus?.filteredReason;
    if (!reason) {
      return null;
    }

    let keyPart = REASON_KEY_MAP[reason] || 'default';
    return this.intl.t(
      `knowledge-hub.filterable-list.list.cells.sync-status.reasons.${keyPart}.message`,
    );
  }

  get showMissingWarning(): boolean {
    let reason = this.args.syncStatus?.filteredReason;
    return (
      (this.args.syncStatus?.syncStatus === SyncStatus.LIVE ||
        this.args.syncStatus?.syncStatus === SyncStatus.PENDING_VERIFICATION) &&
      reason === 'page_missing_in_this_run'
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Table::Cells::SyncStatus': typeof SyncStatusComponent;
    'knowledge-hub/filterable-list/table/cells/sync-status': typeof SyncStatusComponent;
  }
}
